import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import styles from '../MemberFormModal/MemberFormModal.module.css';

import { participationStatusOptions } from 'config/admin/clubMembers';
import {
  createClubMember,
  lookupUserByEmail,
  findClubNameMatches,
  getMembers,
} from 'api/userApi';
import { getGuestMembers } from 'api/guestsApi';

import CustomSelect from 'components/common/CustomSelect';
import UserLookupConfirmDialog from 'components/admin/UserLookupConfirmDialog';
import MemberSearchPicker from 'components/admin/MemberSearchPicker/MemberSearchPicker';
import { ROLE_OPTIONS } from '../MemberFormModal/MemberFormModal';

// Search-first "add a member" dialog. Mirrors the secondary-contact add flow so the admin can't create a
// duplicate of someone who already exists: the live search shows people already in THIS club as blocked rows (the
// same-club, different-email trap), and the create sub-form surfaces OFF-club accounts with the same name to attach
// instead of creating. Only a genuinely new person is created. Guest is no longer offered as a role (the guests feature
// is hidden for every club, 2026-10-11); the hidden Guest Members page still opens this with defaultRole="Guest".
// See docs/secondary-contact-family-ui-plan.html §15.
const AddMemberModal = ({ show, clubId, defaultRole = 'Member', onClose, onSuccess }) => {
  // The picker reports its current choice here: { type: 'existing', person } | { type: 'new', firstName, lastName, email }.
  const [selection, setSelection] = useState(null);
  // Defaults to Member; the Guest Members page opens this with defaultRole="Guest". The modal remounts on each open
  // (it's conditionally rendered), so this initial value is re-applied every time it's opened.
  const [role, setRole] = useState(defaultRole);
  const [participationStatus, setParticipationStatus] = useState('Active');
  const [loading, setLoading] = useState(false);

  // Existing-account preflight (create path): the typed email may already belong to an account.
  const [lookupResult, setLookupResult] = useState(null);
  const [showLookupDialog, setShowLookupDialog] = useState(false);

  // Live in-club directory for the picker's search — members AND guests, so an existing in-club person of either type
  // surfaces (the same-club, different-email trap is the whole point). Memoised so the picker loads it once per club.
  const loadCandidates = useCallback(
    () =>
      Promise.all([getMembers(clubId), getGuestMembers(clubId)]).then(
        ([m, g]) => [...(m?.data || []), ...(g?.data || [])]
      ),
    [clubId]
  );

  // Everyone in the directory is already a club member, so none can be added again — surface them as a blocked match
  // ("already a member") so the admin spots a same-club duplicate before creating another under a different email.
  const memberEligibility = useCallback(
    (p) => ({
      eligible: false,
      reason: `already a member of this club${p.role ? ` (${p.role})` : ''}`,
    }),
    []
  );

  // Duplicate heads-up while typing a name: people already in THIS club who share the exact name (possibly under a
  // different email — the trap we're guarding against). In the create sub-flow the directory search isn't visible, so
  // this heads-up is the only place an in-club duplicate can surface. The server never lists anyone outside the club
  // (Doug, 2026-11-05): someone from elsewhere is found by their email when the admin adds them.
  const findDuplicates = useCallback(
    async ({ firstName, lastName, excludeUserId }) => {
      try {
        const resp = await findClubNameMatches(clubId, firstName, lastName, excludeUserId);
        return resp?.data?.matches || [];
      } catch (e) {
        return [];
      }
    },
    [clubId]
  );

  // Someone already in this club can't be added again — block the heads-up "use this one instead" for them (the
  // server rejects it too, but the admin should see it before clicking).
  const duplicateBlockedReason = useCallback(
    (d) =>
      d.isMemberOfThisClub
        ? `Can’t add — already ${d.thisClubRole === 'Guest' ? 'a guest' : 'a member'} of this club`
        : null,
    []
  );

  const surfaceServerError = (error, fallback) => {
    console.error('Add member error:', error);
    const msg =
      error?.response?.data?.message ||
      error?.response?.data?.Message ||
      error?.response?.data ||
      error?.message ||
      fallback;
    toast.error(typeof msg === 'string' ? msg : fallback);
  };

  // Attaches an EXISTING account by id (the email-preflight match, sent with its email, which the server requires) OR
  // creates a new person by email. The server attaches-vs-creates and rejects an already-in-club user. Role/status
  // apply to the new club membership.
  const addMember = async ({ userID, email, firstName, lastName }) => {
    setLoading(true);
    try {
      await createClubMember({
        clubId,
        role,
        participationStatus,
        userID: userID || undefined,
        email: (email || '').trim(),
        firstName: (firstName || '').trim(),
        lastName: (lastName || '').trim(),
      });
      toast.success('Member added successfully');
      setShowLookupDialog(false);
      setLookupResult(null);
      onSuccess();
    } catch (error) {
      surfaceServerError(error, 'Failed to add member');
    } finally {
      setLoading(false);
    }
  };

  const submitAdd = async () => {
    if (!selection || !selection.valid) return;

    if (selection.type === 'existing') {
      // A pick from the heads-up. It lists only people already in this club, whom the heads-up blocks, so this is not
      // reached in practice; the server refuses an id without the account's email.
      await addMember({ userID: selection.person.userID, email: selection.person.email });
      return;
    }

    // New person: preflight by email — it may already belong to an account, in which case we confirm/attach rather
    // than create a duplicate (the same-email case the name search can't catch).
    setLoading(true);
    try {
      const response = await lookupUserByEmail(clubId, selection.email.trim());
      if (response.data?.exists) {
        setLookupResult(response.data);
        setShowLookupDialog(true);
        setLoading(false);
        return;
      }
      await addMember({
        email: selection.email,
        firstName: selection.firstName,
        lastName: selection.lastName,
      });
    } catch (error) {
      surfaceServerError(error, 'Failed to look up user by email');
      setLoading(false);
    }
  };

  // Confirmed attaching the existing account that matched the typed email — attach by id, with the typed email, which
  // the server requires for someone from outside the club (Doug, 2026-11-05).
  const handleLookupConfirm = async () => {
    await addMember({ userID: lookupResult?.userID, email: selection?.email });
  };

  const handleLookupCancel = () => {
    setShowLookupDialog(false);
    setLookupResult(null);
  };

  if (!show) return null;

  const addDisabled = loading || !selection || !selection.valid;

  return (
    <>
      {/* No overlay click-to-dismiss — closing is deliberate (the × button), so a mis-click mid-add doesn't lose work. */}
      <div className={styles.modalOverlay}>
        <div
          className={styles.modalContent}
          style={{ height: 'min(760px, 92vh)', display: 'flex', flexDirection: 'column' }}
        >
          <div className={styles.modalHeader}>
            <h2 className={styles.modalTitle}>Add Member</h2>
            <button className={styles.closeBtn} onClick={onClose} aria-label="Close">
              &times;
            </button>
          </div>

          <div
            className={styles.modalBody}
            style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
          >
            <div
              className={styles.form}
              style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
            >
              <p style={{ fontSize: '0.85rem', color: '#4b5563', marginTop: 0 }}>
                Search for the person first — they may already be in this club under a different email. Only add a
                new person if there’s genuinely no match.
              </p>

              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <CustomSelect
                    label="Role"
                    options={ROLE_OPTIONS}
                    value={role}
                    name="role"
                    onChange={(e) => setRole(e.target.value)}
                  />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <CustomSelect
                    label="Membership Status"
                    options={participationStatusOptions}
                    value={participationStatus}
                    name="participationStatus"
                    onChange={(e) => setParticipationStatus(e.target.value)}
                  />
                </div>
              </div>

              <MemberSearchPicker
                clubId={clubId}
                loadCandidates={loadCandidates}
                eligibilityCheck={memberEligibility}
                findDuplicates={findDuplicates}
                duplicateBlockedReason={duplicateBlockedReason}
                onChange={setSelection}
                maxResults={8}
                createLabel="Add a new person"
                actionSlot={
                  <button className={styles.btnPrimary} onClick={submitAdd} disabled={addDisabled}>
                    {loading ? 'Saving...' : 'Add Member'}
                  </button>
                }
              />
            </div>
          </div>
        </div>
      </div>

      <UserLookupConfirmDialog
        show={showLookupDialog}
        lookupResult={lookupResult}
        purpose={role === 'Guest' ? 'guest' : 'member'}
        context={{
          selectedRole: role,
          typedFirstName: selection?.firstName,
          typedLastName: selection?.lastName,
        }}
        loading={loading}
        onConfirm={handleLookupConfirm}
        onCancel={handleLookupCancel}
      />
    </>
  );
};

AddMemberModal.propTypes = {
  show: PropTypes.bool.isRequired,
  clubId: PropTypes.string.isRequired,
  defaultRole: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default AddMemberModal;
